import { useMutation } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import HoverTooltip from '@deltek/specification-client/core/Feedbacks/HoverTooltip'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { IconButton, makeStyles } from '@material-ui/core'
import { Check } from '@material-ui/icons'
import ThumbDownIcon from '@material-ui/icons/ThumbDown'
import ThumbUpIcon from '@material-ui/icons/ThumbUp'
import CopyOutlinedIcon from 'components/RightPane/CopyOutlinedIcon'
import RegenerateOutlinedIcon from 'components/RightPane/RegenerateOutlinedIcon'
import ThumbsDownOutlinedIcon from 'components/RightPane/ThumbsDownOutlinedIcon'
import ThumbsUpOutlinedIcon from 'components/RightPane/ThumbsUpOutlinedIcon'
import useBetaWorkspaceToggle from 'pages/ProjectsPage/WorkspacePage/components/WorkspaceRightPane/hooks/useBetaWorkspaceToggle'
import React, { forwardRef, RefObject } from 'react'
import { chatPrimaryColor, chatPrimaryColorBeta } from './colors'
import { Message } from './message'
import { useChatBot } from './useChatBotHook'

export interface MessageActionsStylesProps {
    isBetaWorkspaceToggleEnabled?: boolean
}

const useStyles = makeStyles((theme) => ({
    container: (props: MessageActionsStylesProps) => ({
        height: '22px',
        width: '100%',
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        gap: '4px',
        backgroundColor: props.isBetaWorkspaceToggleEnabled
            ? '#EDF0F6'
            : 'transparent',
    }),
    active: (props: MessageActionsStylesProps) => ({
        fill: props.isBetaWorkspaceToggleEnabled
            ? chatPrimaryColorBeta
            : chatPrimaryColor,
    }),
    actionIcon: {
        fontSize: `${pxToRem(16)} !important`,
    },
}))

export interface MessageActionsProps {
    message: Message
    tabIndex?: number
    ref?: React.Ref<HTMLButtonElement>
    messageContentRef?: RefObject<HTMLDivElement>
}

const MessageActions: React.FC<MessageActionsProps> = forwardRef(
    ({ message, tabIndex, messageContentRef }, ref) => {
        const isBetaWorkspaceToggleEnabled = useBetaWorkspaceToggle()
        const classes = useStyles({ isBetaWorkspaceToggleEnabled })
        const {
            giveFeedback,
            regenerateMessage,
            isLastMessage,
            copyMessageHTML,
        } = useChatBot()

        const [isCopied, setIsCopied] = React.useState<boolean>(false)

        const [showGlobalSnackbar] = useMutation<
            ShowGlobalSnackbarRequest,
            ShowGlobalSnackbarMutationProps
        >(ShowGlobalSnackbarMutation)

        const handleThumbsUp = React.useCallback(() => {
            giveFeedback(message, true)
        }, [message])

        const handleThumbsDown = React.useCallback(() => {
            giveFeedback(message, false)
        }, [message])

        const handleCopyMessage = React.useCallback(async () => {
            // used to animate the message being copied
            if (isCopied) return
            setIsCopied(true)

            // Ensure ref is not null before copying
            if (messageContentRef && messageContentRef.current) {
                // copy the actual message
                const isSuccessful = await copyMessageHTML(
                    messageContentRef.current
                )
                if (!isSuccessful) {
                    showGlobalSnackbar({
                        variables: {
                            message:
                                'Error copying message. Please allow clipboard access from your browser.',
                            variant: 'error',
                            id: `AskDela_CopyMessageError`,
                        },
                    })
                }
            }

            // wait a bit before resetting the copy state
            const copyTimeout = setTimeout(() => {
                setIsCopied(false)
                clearTimeout(copyTimeout)
            }, 1000)
        }, [isCopied, messageContentRef])

        const handleRegenerateMessage = React.useCallback(() => {
            regenerateMessage(message)
        }, [message])

        const showLastMessageActions = React.useMemo(
            () => isLastMessage(message),
            [message, isLastMessage]
        )

        //#region Accessibility

        const handleKeyDown = React.useCallback(
            (
                event: React.KeyboardEvent<HTMLButtonElement>,
                action: 'thumbsUp' | 'thumbsDown' | 'regenerate' | 'copy'
            ) => {
                if (event.key === 'Enter') {
                    event.stopPropagation()
                    event.preventDefault()

                    const callbacks = {
                        thumbsUp: handleThumbsUp,
                        thumbsDown: handleThumbsDown,
                        regenerate: handleRegenerateMessage,
                        copy: handleCopyMessage,
                    }

                    callbacks[action]?.()
                }
            },
            [
                handleThumbsUp,
                handleThumbsDown,
                handleRegenerateMessage,
                handleCopyMessage,
            ]
        )

        //#endregion

        return (
            <div className={classes.container}>
                {showLastMessageActions && (
                    <>
                        <HoverTooltip
                            text="Share Positive Feedback"
                            placements={['bottom']}
                        >
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleThumbsUp}
                                onKeyDown={(e) => handleKeyDown(e, 'thumbsUp')}
                                data-testid="thumbsUp"
                                role="button"
                                aria-label="Share Positive Feedback"
                                tabIndex={tabIndex}
                                ref={ref}
                            >
                                {message.isLiked === true ? (
                                    <ThumbUpIcon
                                        className={classes.actionIcon}
                                    />
                                ) : (
                                    <ThumbsUpOutlinedIcon
                                        className={classes.actionIcon}
                                    />
                                )}
                            </IconButton>
                        </HoverTooltip>

                        <HoverTooltip
                            text="Report Issue"
                            placements={['bottom']}
                        >
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleThumbsDown}
                                onKeyDown={(e) =>
                                    handleKeyDown(e, 'thumbsDown')
                                }
                                data-testid="thumbsDown"
                                role="button"
                                aria-label="Report Issue"
                                tabIndex={tabIndex}
                            >
                                {message.isLiked === false ? (
                                    <ThumbDownIcon
                                        className={classes.actionIcon}
                                    />
                                ) : (
                                    <ThumbsDownOutlinedIcon
                                        className={classes.actionIcon}
                                    />
                                )}
                            </IconButton>
                        </HoverTooltip>

                        <HoverTooltip
                            text="Retry Answer"
                            placements={['bottom']}
                        >
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={handleRegenerateMessage}
                                onKeyDown={(e) =>
                                    handleKeyDown(e, 'regenerate')
                                }
                                data-testid="regenerate"
                                role="button"
                                aria-label="Retry Answer"
                                tabIndex={tabIndex}
                            >
                                <RegenerateOutlinedIcon
                                    className={classes.actionIcon}
                                />
                            </IconButton>
                        </HoverTooltip>
                    </>
                )}

                <HoverTooltip text="Copy Answer" placements={['bottom']}>
                    <IconButton
                        size="small"
                        color="primary"
                        onClick={handleCopyMessage}
                        onKeyDown={(e) => handleKeyDown(e, 'copy')}
                        data-testid="copy"
                        role="button"
                        aria-label="Copy Answer"
                        tabIndex={tabIndex}
                    >
                        {isCopied ? (
                            <Check className={classes.actionIcon} />
                        ) : (
                            <CopyOutlinedIcon className={classes.actionIcon} />
                        )}
                    </IconButton>
                </HoverTooltip>
            </div>
        )
    }
)

export default MessageActions
